import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  isDefinedObject,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import {
  deleteToken,
  getToken,
  onMessage,
  type MessagePayload,
  type Messaging
} from "firebase/messaging";

import {
  firebaseServiceWorkerUrl,
  getFirebaseMessaging
} from "../../services/firebase";

import { firebaseVapidKey } from "../../services/firebase/configuration";

import type {
  FirebaseServiceWorkerMessage,
  TFirebaseMessagingError,
  TFirebaseServiceWorkerMessageData,
  TServiceWorker,
  TServiceWorkerState
} from "./types";

import {
  E_FIREBASE_BROWSER_ERROR_NAME,
  E_FIREBASE_BROWSER_EVENT,
  E_FIREBASE_SERVICE_WORKER_MESSAGE_TYPE,
  E_NOTIFICATION_PERMISSION,
  E_SERVICE_WORKER_STATE,
  E_SERVICE_WORKER_UPDATE_VIA_CACHE,
  FIREBASE_SERVICE_WORKER_ACTIVATION_SETTLE_DELAY_MS,
  FIREBASE_SERVICE_WORKER_ACTIVATION_TIMEOUT_MS,
  FIREBASE_SERVICE_WORKER_SCOPE,
  FIREBASE_TOKEN_MAX_ATTEMPTS,
  FIREBASE_TOKEN_RETRY_DELAY_MS,
  NO_ACTIVE_SERVICE_WORKER_ERROR_MESSAGE
} from "./constants";

import { getNotificationStore } from "../notification-store";
import getCorePersonalStore from "../core/core-personal-store";
import getCorePopUpStore from "../core/core-popup-store";
import { apiCallUpdateFcmToken } from "../../api/core-api/api-call-update-fcm-token";

class FirebaseStore {
  /**
   * Observable values represent Firebase state that UI components
   * may need to display or react to.
   */
  fcmToken = observable.box<string>("");

  isFirebaseMessagingInitializing = observable.box<boolean>(false);

  isFirebaseMessagingInitialized = observable.box<boolean>(false);

  firebaseMessagingError = observable.box<string>("");

  /**
   * Private concurrency lock.
   *
   * Prevents multiple Firebase initialization requests from running
   * simultaneously.
   */
  private initializationPromise: Promise<string> | null = null;

  /**
   * Private Firebase foreground-listener cleanup callback.
   */
  private unsubscribeForegroundMessaging: (() => void) | null = null;

  /**
   * Prevents registering the service-worker message listener more
   * than once.
   */
  private isServiceWorkerMessageListenerRegistered = false;

  /**
   * Prevents asynchronous initialization from registering listeners
   * after the application has already unmounted.
   */
  private isMessagingLifecycleActive = false;

  refreshNotificationPermission = (): NotificationPermission =>
    reduce(() => Notification.permission, E_NOTIFICATION_PERMISSION.DEFAULT);

  registerIfNotificationPermissionGranted = async (): Promise<void> => {
    if (
      this.refreshNotificationPermission() !==
      E_NOTIFICATION_PERMISSION.GRANTED
    ) {
      return;
    }

    try {
      this.validateBrowserSupport();

      await this.startFirebaseMessaging();

      if (isPositiveNumber(getCorePersonalStore().user.get().id)) {
        await this.sendFcmTokenToServer();
      }
    } catch (error: unknown) {
      this.handleFirebaseMessagingError(<TFirebaseMessagingError>error);
    }
  };

  enableNotifications = async (): Promise<void> => {
    getCorePopUpStore().answerNotificationPermissionPopUp();

    try {
      this.validateBrowserSupport();

      const permission: NotificationPermission =
        await Notification.requestPermission();

      if (permission !== E_NOTIFICATION_PERMISSION.GRANTED) {
        return;
      }
    } catch (error: unknown) {
      this.handleFirebaseMessagingError(<TFirebaseMessagingError>error);

      return;
    }

    await this.registerIfNotificationPermissionGranted();
  };

  sendFcmTokenToServer = async (): Promise<void> => {
    const fcmToken: string = reduce(() => this.fcmToken.get(), "");

    if (!isDefinedString(fcmToken)) {
      return;
    }

    const { status, code } = await apiCallUpdateFcmToken({ fcmToken });

    if (status !== "OK") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  /**
   * Returns the existing FCM token when Firebase has already been
   * initialized.
   *
   * It also ensures that the Firebase message listeners are active.
   */
  startFirebaseMessaging = async (): Promise<string> => {
    this.isMessagingLifecycleActive = true;

    const existingToken: string = reduce(() => this.fcmToken.get(), "");

    if (existingToken) {
      const messaging = await getFirebaseMessaging();

      if (messaging) {
        this.registerFirebaseMessageListeners(messaging);
      }

      return existingToken;
    }

    const existingInitializationPromise: Promise<string> | null = reduce(
      () => this.initializationPromise,
      null
    );

    if (existingInitializationPromise) {
      return existingInitializationPromise;
    }

    this.initializationPromise = this.initializeFirebaseMessaging();

    try {
      return await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  };

  /**
   * Registers the service worker, initializes Firebase Messaging,
   * creates the FCM token, and registers the message listeners.
   */
  private initializeFirebaseMessaging = async (): Promise<string> => {
    runInAction(() => {
      this.isFirebaseMessagingInitializing.set(true);

      this.isFirebaseMessagingInitialized.set(false);

      this.firebaseMessagingError.set("");
    });

    try {
      this.validateBrowserSupport();

      const permission: NotificationPermission = reduce(
        () => Notification.permission,
        E_NOTIFICATION_PERMISSION.DEFAULT
      );

      if (permission !== E_NOTIFICATION_PERMISSION.GRANTED) {
        throw new Error(
          "Notification permission is required to initialize Firebase Messaging."
        );
      }

      const serviceWorkerRegistration =
        await this.registerFirebaseServiceWorker();

      const messaging = await getFirebaseMessaging();

      if (!messaging) {
        throw new Error("Firebase Messaging is not supported by this browser.");
      }

      const token = await this.generateFirebaseToken({
        messaging,
        serviceWorkerRegistration
      });

      if (!token) {
        throw new Error("Firebase did not return an FCM token.");
      }

      if (this.isMessagingLifecycleActive) {
        this.registerFirebaseMessageListeners(messaging);
      }

      runInAction(() => {
        this.fcmToken.set(token);

        this.isFirebaseMessagingInitialized.set(true);

        this.firebaseMessagingError.set("");
      });

      return token;
    } finally {
      runInAction(() => {
        this.isFirebaseMessagingInitializing.set(false);
      });
    }
  };

  /**
   * Registers foreground and background Firebase message channels.
   */
  private registerFirebaseMessageListeners = (messaging: Messaging): void => {
    if (!this.isMessagingLifecycleActive) {
      return;
    }

    if (!this.isServiceWorkerMessageListenerRegistered) {
      navigator.serviceWorker.addEventListener(
        E_FIREBASE_BROWSER_EVENT.MESSAGE,
        this.handleServiceWorkerMessage
      );

      this.isServiceWorkerMessageListenerRegistered = true;
    }

    const unsubscribeForegroundMessaging: () => void = reduce(
      () => this.unsubscribeForegroundMessaging,
      null
    );

    if (!unsubscribeForegroundMessaging) {
      this.unsubscribeForegroundMessaging = onMessage(
        messaging,
        async (payload: MessagePayload): Promise<void> => {
          await this.forwardFirebaseMessageToNotificationStore(payload);
        }
      );
    }
  };

  /**
   * Receives Firebase messages forwarded by the service worker.
   */
  private handleServiceWorkerMessage = async (
    event: MessageEvent<TFirebaseServiceWorkerMessageData>
  ): Promise<void> => {
    const messageData: TFirebaseServiceWorkerMessageData = reduce(
      () => event.data,
      {
        type: "",
        payload: {}
      }
    );

    if (!this.isFirebaseServiceWorkerMessage(messageData)) {
      return;
    }

    const payload: MessagePayload = reduce(
      () => messageData.payload,
      messageData.payload
    );

    await this.forwardFirebaseMessageToNotificationStore(payload);
  };

  /**
   * Validates that the service-worker message has the expected
   * Firebase message wrapper.
   */
  private isFirebaseServiceWorkerMessage = (
    data: TFirebaseServiceWorkerMessageData
  ): data is FirebaseServiceWorkerMessage => {
    if (!isDefinedObject(data)) {
      return false;
    }

    const possibleMessageType: string = reduce(() => data.type, "");

    const possibleMessagePayload: object | null = reduce(
      () => data.payload,
      null
    );

    return (
      possibleMessageType ===
        E_FIREBASE_SERVICE_WORKER_MESSAGE_TYPE.BACKGROUND_MESSAGE &&
      isDefinedObject(possibleMessagePayload)
    );
  };

  /**
   * Extracts the business notification from the Firebase notification
   * body and forwards it to NotificationStore.
   */
  private forwardFirebaseMessageToNotificationStore = async (
    payload: MessagePayload
  ): Promise<void> => {
    const body: string = reduce(
      () => payload.data.body ?? payload.notification.body,
      ""
    );
    if (!isDefinedString(body)) {
      return;
    }

    try {
      await getNotificationStore().handleFirebaseNotificationBody({
        body
      });
    } catch (error: unknown) {}
  };

  /**
   * Removes all Firebase message listeners.
   */
  stopFirebaseMessaging = (): void => {
    this.isMessagingLifecycleActive = false;

    if (this.isServiceWorkerMessageListenerRegistered) {
      navigator.serviceWorker.removeEventListener(
        E_FIREBASE_BROWSER_EVENT.MESSAGE,
        this.handleServiceWorkerMessage
      );

      this.isServiceWorkerMessageListenerRegistered = false;
    }

    const unsubscribeForegroundMessaging: () => void = reduce(
      () => this.unsubscribeForegroundMessaging,
      null
    );

    if (unsubscribeForegroundMessaging) {
      unsubscribeForegroundMessaging();

      this.unsubscribeForegroundMessaging = null;
    }
  };

  /**
   * Registers the Firebase service worker and waits until it is
   * fully activated.
   */
  private registerFirebaseServiceWorker =
    async (): Promise<ServiceWorkerRegistration> => {
      const registration = await navigator.serviceWorker.register(
        firebaseServiceWorkerUrl,
        {
          scope: FIREBASE_SERVICE_WORKER_SCOPE,
          updateViaCache: E_SERVICE_WORKER_UPDATE_VIA_CACHE.NONE
        }
      );

      return await this.waitForActivatedServiceWorker(registration);
    };

  /**
   * Waits until the Firebase service worker is activated.
   */
  private waitForActivatedServiceWorker = async (
    registration: ServiceWorkerRegistration
  ): Promise<ServiceWorkerRegistration> => {
    const activeWorkerState: TServiceWorkerState = reduce(
      () => registration.active.state,
      null
    );

    if (activeWorkerState === E_SERVICE_WORKER_STATE.ACTIVATED) {
      return registration;
    }

    const worker: TServiceWorker = reduce(
      () =>
        registration.active ?? registration.installing ?? registration.waiting,
      null
    );

    if (isNullOrUndefined(worker)) {
      throw new Error("Firebase service worker registration has no worker.");
    }

    await new Promise<void>((resolve, reject) => {
      let timeoutId: number;

      const cleanup = (): void => {
        worker.removeEventListener(
          E_FIREBASE_BROWSER_EVENT.STATE_CHANGE,
          handleStateChange
        );

        const currentTimeoutId: number = reduce(() => timeoutId, -1);

        if (isPositiveNumber(currentTimeoutId)) {
          window.clearTimeout(currentTimeoutId);
        }
      };

      const handleStateChange = (): void => {
        const workerState: TServiceWorkerState = reduce(
          () => worker.state,
          null
        );

        if (workerState === E_SERVICE_WORKER_STATE.ACTIVATED) {
          cleanup();

          resolve();

          return;
        }

        if (workerState === E_SERVICE_WORKER_STATE.REDUNDANT) {
          cleanup();

          reject(
            new Error(
              "Firebase service worker became redundant before activation."
            )
          );
        }
      };

      worker.addEventListener(
        E_FIREBASE_BROWSER_EVENT.STATE_CHANGE,
        handleStateChange
      );

      timeoutId = window.setTimeout(() => {
        cleanup();

        const workerState: TServiceWorkerState = reduce(
          () => worker.state,
          null
        );

        reject(
          new Error(
            `Timed out waiting for Firebase service worker activation. Current state: ${
              workerState ?? "unknown"
            }.`
          )
        );
      }, FIREBASE_SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

      handleStateChange();
    });

    await this.delay(FIREBASE_SERVICE_WORKER_ACTIVATION_SETTLE_DELAY_MS);

    const registrationScope: string = reduce(
      () => registration.scope,
      FIREBASE_SERVICE_WORKER_SCOPE
    );

    const refreshedRegistration =
      await navigator.serviceWorker.getRegistration(registrationScope);

    const finalRegistration: ServiceWorkerRegistration = reduce(
      () => refreshedRegistration,
      registration
    );

    const finalActiveWorkerState: TServiceWorkerState = reduce(
      () => finalRegistration.active.state,
      null
    );

    if (finalActiveWorkerState !== E_SERVICE_WORKER_STATE.ACTIVATED) {
      const currentState: TServiceWorkerState = reduce(
        () => finalRegistration.active.state,
        null
      );

      throw new Error(
        `Firebase service worker is not fully activated. Current state: ${
          currentState ?? "none"
        }.`
      );
    }

    return finalRegistration;
  };

  /**
   * Generates the FCM registration token.
   */
  private generateFirebaseToken = async ({
    messaging,
    serviceWorkerRegistration
  }: {
    messaging: Messaging;
    serviceWorkerRegistration: ServiceWorkerRegistration;
  }): Promise<string> => {
    let registration = serviceWorkerRegistration;

    for (
      let attempt = 1;
      attempt <= FIREBASE_TOKEN_MAX_ATTEMPTS;
      attempt += 1
    ) {
      try {
        const activeWorkerState: TServiceWorkerState = reduce(
          () => registration.active.state,
          null
        );

        if (activeWorkerState !== E_SERVICE_WORKER_STATE.ACTIVATED) {
          registration = await this.waitForActivatedServiceWorker(registration);
        }

        return await getToken(messaging, {
          vapidKey: firebaseVapidKey,
          serviceWorkerRegistration: registration
        });
      } catch (error: unknown) {
        const canRetry =
          attempt < FIREBASE_TOKEN_MAX_ATTEMPTS &&
          this.isNoActiveServiceWorkerError(<TFirebaseMessagingError>error);

        if (!canRetry) {
          throw error;
        }

        await this.delay(FIREBASE_TOKEN_RETRY_DELAY_MS);

        registration = await this.registerFirebaseServiceWorker();
      }
    }

    throw new Error("Firebase token generation failed.");
  };

  /**
   * Checks whether token creation failed because Chrome had not yet
   * recognized the activated service worker.
   */
  private isNoActiveServiceWorkerError = (
    error: TFirebaseMessagingError
  ): boolean => {
    const name: string = reduce(() => error.name, "");

    const message: string = reduce(() => error.message.toLowerCase(), "");

    return (
      name === E_FIREBASE_BROWSER_ERROR_NAME.ABORT_ERROR &&
      message.includes(NO_ACTIVE_SERVICE_WORKER_ERROR_MESSAGE)
    );
  };

  /**
   * Internal delay helper.
   */
  private delay = (milliseconds: number): Promise<void> =>
    new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });

  /**
   * Validates browser support required by Firebase Cloud Messaging.
   */
  private validateBrowserSupport = (): void => {
    const isSecureContext: boolean = reduce(
      () => window.isSecureContext,
      false
    );

    if (!isSecureContext) {
      throw new Error("Firebase Cloud Messaging requires HTTPS or localhost.");
    }

    const supportsNotifications: boolean = reduce(
      () => "Notification" in window,
      false
    );

    if (!supportsNotifications) {
      throw new Error("Notifications are not supported by this browser.");
    }

    const supportsServiceWorkers: boolean = reduce(
      () => "serviceWorker" in navigator,
      false
    );

    if (!supportsServiceWorkers) {
      throw new Error("Service workers are not supported by this browser.");
    }

    const supportsPushManager: boolean = reduce(
      () => "PushManager" in window,
      false
    );

    if (!supportsPushManager) {
      throw new Error("Push notifications are not supported by this browser.");
    }
  };

  /**
   * Stores Firebase initialization errors in observable state.
   */
  private handleFirebaseMessagingError = (
    error: TFirebaseMessagingError
  ): void => {
    const message: string = reduce(
      () => error.message,
      "Unknown Firebase Messaging error."
    );

    console.warn("Firebase Messaging initialization failed:", message);

    runInAction(() => {
      this.firebaseMessagingError.set(message);

      this.isFirebaseMessagingInitialized.set(false);
    });
  };

  invalidateFirebaseToken = async (): Promise<void> => {
    this.stopFirebaseMessaging();

    const existingToken: string = reduce(() => this.fcmToken.get(), "");

    try {
      if (isDefinedString(existingToken)) {
        const messaging = await getFirebaseMessaging();

        if (messaging) {
          await deleteToken(messaging);
        }
      }
    } catch (error: unknown) {
      this.handleFirebaseMessagingError(<TFirebaseMessagingError>error);
    } finally {
      this.initializationPromise = null;

      runInAction(() => {
        this.fcmToken.set("");

        this.isFirebaseMessagingInitialized.set(false);

        this.firebaseMessagingError.set("");
      });
    }
  };
}

const getFirebaseStore = memoize(
  () => new FirebaseStore(),
  () => 1
);

export default getFirebaseStore;
